import React from 'react';
import type { PostResponseSchema } from '@/api/client';

export type MediaInfo = {
  type: 'image' | 'video' | 'audio';
  src: string;
};

interface Props {
  post: PostResponseSchema;
  onMediaClick: (media: MediaInfo) => void;
}

export function PostContent({ post, onMediaClick }: Props) {
  const imageUrl = post.photo_url || post.photo_url_compressed;
  const videoUrl = post.m3u8_url;
  const audioUrl = post.audio_url;

  const hasImage = !!imageUrl && !videoUrl;
  const hasVideo = !!videoUrl;
  const hasAudio = !!audioUrl;
  const hasNoMedia = !hasImage && !hasVideo && !hasAudio;

  if (hasNoMedia) {
    return <span style={{ color: 'var(--admin-fg-faint)' }}>-</span>;
  }

  const thumbnailUrl = post.photo_url_compressed || post.photo_url;

  return (
    <div className="admin-media-cell">
      {hasImage && (
        <button
          className="admin-media-thumb"
          onClick={() => onMediaClick({ type: 'image', src: imageUrl })}
          type="button"
          title="View image"
        >
          <img src={imageUrl} alt="Post" />
        </button>
      )}

      {hasVideo && (
        <button
          className="admin-media-thumb"
          onClick={() => onMediaClick({ type: 'video', src: videoUrl })}
          type="button"
          title="Play video"
        >
          {thumbnailUrl ? (
            <img src={thumbnailUrl} alt="Video thumbnail" />
          ) : (
            <VideoIcon />
          )}
          <span className="admin-media-play-icon">
            <PlayIcon />
          </span>
        </button>
      )}

      {hasAudio && (
        <button
          className="admin-media-thumb"
          onClick={() => onMediaClick({ type: 'audio', src: audioUrl })}
          type="button"
          title="Play audio"
        >
          <AudioIcon />
        </button>
      )}
    </div>
  );
}

function PlayIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <path d="M6.79 5.093A.5.5 0 0 0 6 5.5v5a.5.5 0 0 0 .79.407l3.5-2.5a.5.5 0 0 0 0-.814l-3.5-2.5z"/>
    </svg>
  );
}

function VideoIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" style={{ opacity: 0.4 }}>
      <path d="M0 1a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1V1zm4 0v6h8V1H4zm8 8H4v6h8V9zM1 1v2h2V1H1zm2 3H1v2h2V4zM1 7v2h2V7H1zm2 3H1v2h2v-2zm-2 3v2h2v-2H1zM15 1h-2v2h2V1zm-2 3v2h2V4h-2zm2 3h-2v2h2V7zm-2 3v2h2v-2h-2zm2 3h-2v2h2v-2z"/>
    </svg>
  );
}

function AudioIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" style={{ opacity: 0.4 }}>
      <path d="M6 13c0 1.105-1.12 2-2.5 2S1 14.105 1 13c0-1.104 1.12-2 2.5-2s2.5.896 2.5 2zm9-2c0 1.105-1.12 2-2.5 2s-2.5-.895-2.5-2 1.12-2 2.5-2 2.5.895 2.5 2z"/>
      <path fillRule="evenodd" d="M14 11V2h1v9h-1zM6 3v10H5V3h1z"/>
      <path d="M5 2.905a1 1 0 0 1 .9-.995l8-.8a1 1 0 0 1 1.1.995V3L5 4V2.905z"/>
    </svg>
  );
}
